<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">Feedback, Bugs & Feature Requests</h2>

    <div class="mb-6">
      <p>Spin the Names is maintained by one developer, so feedback from users has an outsized impact on what
        gets built and fixed. If something isn't working or you have an idea that would make the tool more useful,
        reaching out genuinely makes a difference.</p>
      <p class="mt-2">All feedback goes to <a href="mailto:ash@spinthenames.com"
          class="text-blue-500 hover:underline">ash@spinthenames.com</a>.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Reporting a bug</h3>
      <p>The more specific you can be, the faster something can be fixed. Helpful details to include:</p>
      <ul class="list-disc ml-6 mt-2">
        <li><strong>What happened</strong> — describe the unexpected behavior as clearly as you can</li>
        <li><strong>What you expected to happen</strong> — what the correct behavior should be</li>
        <li><strong>Steps to reproduce</strong> — what you did before the issue occurred</li>
        <li><strong>Device and browser</strong> — e.g., iPhone 14 / Safari, Windows 11 / Chrome, iPad / Firefox</li>
        <li><strong>Screenshots or video</strong> — if you can capture the issue, it helps enormously</li>
      </ul>
      <p class="mt-2">A report like "the wheel doesn't work" is hard to act on. A report like "the wheel
        freezes after the second spin on iPad Safari when I have more than 20 names" gets fixed much faster.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Requesting a feature</h3>
      <p>Feature requests are welcome. The most useful ones explain the underlying need, not just the solution.
        Instead of "add a confetti animation," something like "I use this for classroom celebrations and it
        would help if there were a more dramatic win moment for students" gives more to work with.</p>
      <p class="mt-2">Good feature requests include:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>What you're trying to accomplish and why the current tool doesn't fully support it</li>
        <li>How often you'd use this feature and in what context</li>
        <li>Any examples of how other tools handle similar needs (useful for reference, not required)</li>
      </ul>
      <p class="mt-2">Not every request becomes a feature — especially if it's complex to build or only
        useful to a small number of people — but all of them are read and considered.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">What's actually built from feedback?</h3>
      <p>Several of the app's current features came directly from user feedback: multilingual support, saved
        wheels, embedding options, and various customization settings all started as requests from teachers,
        event organizers, and streamers who explained what they needed and why.</p>
      <p class="mt-2">The most impactful requests tend to come from people who use the app regularly in a
        specific context — a teacher who runs it every day for class, an event coordinator who uses it monthly
        for giveaways. If that's you, your input carries real weight.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Response times</h3>
      <p>Spin the Names is maintained by an independent developer alongside other work. Bug reports for
        significant issues are typically addressed quickly. Feature requests and general feedback are read
        and may be acknowledged, but a personal reply isn't always possible. If something is genuinely
        broken for you, lead with that — it gets prioritized.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Positive feedback</h3>
      <p>If the app helped your class, made your event go smoothly, or just saved you an awkward
        moment deciding who does the dishes — sending a quick note is appreciated more than you might think.
        Independent software often runs quietly in the background, and hearing it's actually useful to real
        people is genuinely motivating.</p>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
